import i18n from 'i18next';

import {
    RouteCardMetricValue,
    ValueWithUnit
} from '~/ui/components/RouteCard/RouteCardMetric/types';
import dateUtilsConverters from '~/utils/date-utils-converters';
import filterUtils from '~/utils/filter-utils';
import { CompartmentStat } from '~/api/types';
import constants from '~/utils/constants';
import { RouteTripSummary } from '~/components/MapPageDrawers/SelectedCardsDrawer/types';
import generalUtils from '~/utils/general-utils';
import unitsUtils from '~/utils/unit-converter';

export const getTitleLine = ({
    clientName,
    driverName,
    isPlanned,
    vehicleEid,
    isAppendedRoute = false
}: {
    clientName?: string;
    driverName?: string;
    isPlanned?: boolean;
    vehicleEid?: string;
    isAppendedRoute?: boolean;
}): string => {
    const translationKey = isAppendedRoute ? 'routeNameOnly' : 'routeAndName';

    if (!isPlanned) {
        return filterUtils.getInitials(clientName || '');
    }

    return i18n.t(translationKey, {
        driverName,
        ns: 'translation',
        routeName: vehicleEid
    });
};

const getDurationValuesWithUnits = (routeDuration: number) => {
    const { hours, minutes } =
        dateUtilsConverters.convertMillisecondsToHoursAndMinutesAndSeconds(
            routeDuration
        );
    const ns = 'common';
    const hourUnit = i18n
        .t('time.h', {
            ns,
            time: ''
        })
        .trim();
    const minutesUnit = i18n
        .t('time.min', {
            ns,
            time: ''
        })
        .trim();
    const hourValueAndUnit = hours
        ? {
              value: hours.toString(),
              unit: hourUnit
          }
        : null;
    const minuteValueAndUnit = minutes
        ? {
              value: minutes.toString(),
              unit: minutesUnit
          }
        : null;

    return [hourValueAndUnit, minuteValueAndUnit].filter(
        Boolean
    ) as ValueWithUnit[];
};

export type Stats = {
    distance: number;
    duration: number;
    numTasks: number;
    volumeCapacityUsed: number;
    compartmentStats: CompartmentStat[];
};

const getVolumeCapacityUsedValueAndUnit = (count: number) => {
    const unit = i18n.t('unit', {
        ns: 'translation',
        count
    });

    return {
        unit,
        value: count.toString()
    };
};

const getStopsValueAndUnit = (count: number) => {
    const unit = i18n.t('stop', {
        ns: 'translation',
        count
    });

    return {
        unit,
        value: count.toString()
    };
};

const getDistanceValueAndUnit = (totalMeters: number) => {
    const unitKey = unitsUtils.getLocalDistanceUnits({ useAbbreviated: true });
    const unit = i18n.t(unitKey, { distance: '' }).trim();
    const distance = unitsUtils.getLocalDistance(totalMeters);

    return {
        unit,
        value: distance
    };
};

const calculateExceededCapacity = (
    usedCapacity: number,
    maxCapacity: number
) => {
    const diff = generalUtils.roundToMaxDigitsAfterDecimal(
        usedCapacity - maxCapacity,
        2
    );

    return diff > 0 ? diff : 0;
};

const getVolumeMetric = ({
    volumeCapacityUsed,
    maxVolumeCapacity
}: {
    volumeCapacityUsed: number;
    maxVolumeCapacity?: number;
}) => {
    const volumeCapacityUsedValueAndUnit =
        getVolumeCapacityUsedValueAndUnit(volumeCapacityUsed);
    const maxVolume =
        !maxVolumeCapacity && maxVolumeCapacity !== 0
            ? volumeCapacityUsed
            : maxVolumeCapacity;
    const exceededCapacity = calculateExceededCapacity(
        volumeCapacityUsed,
        maxVolume
    );
    const isOverCapacity = Boolean(exceededCapacity);
    const tooltipText = isOverCapacity
        ? i18n.t('overCapacityBy', {
              ns: 'translation',
              count: exceededCapacity
          })
        : undefined;

    return {
        ...(isOverCapacity && {
            isOverCapacity,
            tooltipText
        }),
        metric: constants.routeSummaryMetrics.VOLUME,
        valuesWithUnits: [volumeCapacityUsedValueAndUnit]
    };
};

export const getSummaryMetrics = ({
    stats,
    isMultiCompartmentClient,
    maxVolumeCapacity
}: {
    stats: Stats;
    isMultiCompartmentClient?: boolean;
    maxVolumeCapacity?: number;
}): RouteCardMetricValue[] => {
    const {
        distance,
        duration,
        volumeCapacityUsed,
        numTasks: numStops
    } = stats;
    const distanceValueAndUnit = getDistanceValueAndUnit(distance);
    const durationValuesWithUnits = getDurationValuesWithUnits(duration);
    const stopsValueAndUnit = getStopsValueAndUnit(numStops);

    const metrics = [
        {
            metric: constants.routeSummaryMetrics.STOPS,
            valuesWithUnits: [stopsValueAndUnit]
        },
        {
            metric: constants.routeSummaryMetrics.DISTANCE,
            valuesWithUnits: [distanceValueAndUnit]
        },
        {
            metric: constants.routeSummaryMetrics.DURATION,
            valuesWithUnits: durationValuesWithUnits
        },
        getVolumeMetric({
            volumeCapacityUsed,
            maxVolumeCapacity
        })
    ];

    return metrics.filter((item) => {
        if (!isMultiCompartmentClient) {
            return item;
        }
        return item.metric !== constants.routeSummaryMetrics.VOLUME;
    });
};

export const getTripCompartmentStatsByRouteId = (
    trips: RouteTripSummary[],
    routeId: string
): CompartmentStat[] | undefined => {
    const matchingTrip = (trips || []).find((trip) => trip.routeId === routeId);
    return matchingTrip?.stats?.compartmentStats;
};
